// ProductionLine.js
import React, { useEffect, useMemo, useState } from "react";
import API from "../services/api";
import "./ProductionLine.css";
import FlexibleControl from "../components/FlexibleControl";

/**
 * کامپوننت کنترل تگ دیجیتال
 * وضعیت (Status) هر تگ از prop latestTags گرفته می‌شود.
 */
const TagControl = ({ tagName, valveTag, onToggle, onlyOn = false, latestTags }) => {
  const tagData = latestTags.find((tag) => tag.tag_name === valveTag);
  const status = tagData ? tagData.last_value : "N/A";

  return (
    <div className="box">
      <h3>{tagName}</h3>
      <p>
        Status: <strong>{status}</strong>
      </p>
      <div className="button-group">
        <button onClick={() => onToggle(true)}>ON</button>
        {!onlyOn && <button onClick={() => onToggle(false)}>OFF</button>}
      </div>
    </div>
  );
};

/**
 * کامپوننت کنترل تگ آنالوگ
 * - مقدار فعلی از latestTags نمایش داده می‌شود
 * - مقدار جدید با /set-tag-value/ (tag_id + value) ارسال می‌شود
 */
const AnalogTagControl = ({
  tagName,
  displayTagName,
  tagId,
  min = 0,
  max = 100,
  step = 0.1,
  latestTags,
  onAfterWrite,
}) => {
  const tagData = latestTags.find((tag) => tag.tag_name === displayTagName);

  const currentValue = tagData?.value_kind === "string"
    ? (tagData.last_value_is_null ? "(null)" : (tagData.last_value === "" ? "(empty)" : String(tagData.last_value ?? "N/A")))
    : (
      tagData && typeof tagData.last_value === "number"
        ? tagData.last_value.toFixed(2)
        : "N/A"
    );

  const [value, setValue] = useState("");

  useEffect(() => {
    if (currentValue !== "N/A") {
      setValue(currentValue);
    }
  }, [currentValue]);

  const safeNumber = (v, fallback) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const writeAnalog = async () => {
    if (!tagId) {
      alert(`Tag ID not found for "${displayTagName}".`);
      return;
    }
    if (value === "" || value === null || value === undefined) {
      alert("Please enter a value.");
      return;
    }

    try {
      await API.post("/set-tag-value/", { tag_id: tagId, value: Number(value) });
      onAfterWrite?.();
    } catch (err) {
      console.error("Error writing analog tag:", err);
      console.error("Server response:", err?.response?.data);
      alert("Failed to write analog value.\n" + JSON.stringify(err?.response?.data));
    }
  };

  return (
    <div className="box">
      <h3>{tagName}</h3>

      <p>
        Current Value:&nbsp;
        <strong style={{ fontSize: "1.15em" }}>{currentValue}</strong>
      </p>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          style={{ width: 140 }}
        />

        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={safeNumber(value, min)}
          onChange={(e) => setValue(e.target.value)}
          style={{ width: 220 }}
        />

        <button onClick={writeAnalog}>SET</button>
      </div>

      <small style={{ opacity: 0.8 }}>
        Range: {min} … {max} (step {step})
      </small>
    </div>
  );
};

const ProductionLine = () => {
  const [latestTags, setLatestTags] = useState([]);
  const [tags, setTags] = useState([]);

  // name -> id (برای آنالوگ‌ها)
  const tagIdByName = useMemo(() => {
    const m = new Map();
    tags.forEach((t) => m.set(t.name, t.id));
    return m;
  }, [tags]);

  const fetchLatestTags = async () => {
    try {
      const response = await API.get("/latest-tag-values/");
      setLatestTags(response.data);
    } catch (error) {
      console.error("Error fetching latest tag values:", error);
    }
  };

  const fetchTagsList = async () => {
    try {
      const res = await API.get("/tags/");
      setTags(res.data);
    } catch (e) {
      console.error("Error fetching tags list:", e);
    }
  };

  useEffect(() => {
    fetchTagsList();
    fetchLatestTags();

    const interval = setInterval(fetchLatestTags, 3000);
    return () => clearInterval(interval);
  }, []);

  // دیجیتال: ارسال فرمان تغییر مقدار
  const controlTag = async (tag, value) => {
    try {
      await API.post("/write-tag/", { tag_name: tag, value });
      console.log(`Set ${tag} to ${value}`);
      setTimeout(() => {
        fetchLatestTags();
      }, 200);
    } catch (error) {
      console.error(`Error setting ${tag}:`, error);
    }
  };

  return (
    <div className="production-line-container">
      <h1>Production Line</h1>

      <section className="section-control">
        <h2>Heater Control</h2>
        <div className="grid">
          <TagControl
            tagName="Bypass"
            valveTag="XV-009"
            onlyOn
            onToggle={() => controlTag("STOP_OIL", true)}
            latestTags={latestTags}
          />

          <TagControl
            tagName="Old Reactor"
            valveTag="XV-008"
            onToggle={(v) => controlTag("Oil_Valve_3_G50", v)}
            latestTags={latestTags}
          />

          <TagControl
            tagName="Feeding Tank"
            valveTag="XV-007"
            onToggle={(v) => controlTag("Oil_Valve_2_SS20", v)}
            latestTags={latestTags}
          />

          <TagControl
            tagName="Main Reactor"
            valveTag="XV-006"
            onToggle={(v) => controlTag("Oil_Valve_1_SS100", v)}
            latestTags={latestTags}
          />
        </div>
      </section>

      <section className="section-control">
        <h2>Pumps Control</h2>
        <div className="grid">
          <TagControl
            tagName="Water Pump"
            valveTag="P-001"
            onToggle={(v) => controlTag("Water_Pump", v)}
            latestTags={latestTags}
          />

          <TagControl
            tagName="Feeding Pump"
            valveTag="P-002"
            onToggle={(v) => controlTag("Feeding_Pump", v)}
            latestTags={latestTags}
          />
        </div>
      </section>

      <section className="section-control">
        <h2>Dosing Control</h2>
        <div className="grid">
          {/* آنالوگ */}
          <AnalogTagControl
            tagName="Dosing Water Target mL"
            displayTagName="Dosing_Water_Target"
            tagId={tagIdByName.get("Dosing_Water_Target")}
            min={0}
            max={10000}
            step={0.1}
            latestTags={latestTags}
            onAfterWrite={fetchLatestTags}
          />

          {/* مانیتور + دکمه‌های دیجیتال */}
          <FlexibleControl
            title="Dosing Run"
            latestTags={latestTags}
            onCommand={controlTag}
            monitorItems={[
              { label: "Active", tag: "Dosing_Active" },
              { label: "Mode", tag: "Dosing_Status" },
              { label: "Collected water", tag: "Dosing Colected" }, // فقط مانیتور
            ]}
            buttons={[
              { label: "START", tag: "Dosing_Start", value: true }, // دیجیتال
              { label: "RESET", tag: "Dosing_Reset", value: true }, // دیجیتال
            ]}
          />
        </div>
      </section>
    </div>
  );
};

export default ProductionLine;
